import { labelTextHulls, type LabelHull, type OutlinePoint as Point } from './labelOutline';
import type { AtomLabelBox } from './labelMetrics';

export interface ChargeAtom {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly text: string;
  readonly offsetX: number;
  readonly hulls: readonly LabelHull[];
  readonly radical?: string;
}
export interface ChargeBond {
  readonly from: Point;
  readonly to: Point;
  readonly radius: number;
}
export interface ChargePlacement extends Point {
  readonly box: AtomLabelBox;
}

function bounds(points: readonly Point[]): AtomLabelBox {
  const xs = points.map((p) => p.x),
    ys = points.map((p) => p.y);
  const x = Math.min(...xs),
    y = Math.min(...ys);
  return { maskX: x, maskY: y, maskWidth: Math.max(...xs) - x, maskHeight: Math.max(...ys) - y };
}
function expanded(box: AtomLabelBox, pad: number): AtomLabelBox {
  return {
    maskX: box.maskX - pad,
    maskY: box.maskY - pad,
    maskWidth: box.maskWidth + pad * 2,
    maskHeight: box.maskHeight + pad * 2
  };
}
function overlap(a: AtomLabelBox, b: AtomLabelBox): number {
  return (
    Math.max(0, Math.min(a.maskX + a.maskWidth, b.maskX + b.maskWidth) - Math.max(a.maskX, b.maskX)) *
    Math.max(0, Math.min(a.maskY + a.maskHeight, b.maskY + b.maskHeight) - Math.max(a.maskY, b.maskY))
  );
}
/** Segment/rectangle clipping catches a bond crossing anywhere through a wide charge label. */
export function bondCrossesCharge(bond: ChargeBond, box: AtomLabelBox): boolean {
  const rect = expanded(box, bond.radius);
  let lo = 0,
    hi = 1;
  for (const [a, b, min, max] of [
    [bond.from.x, bond.to.x, rect.maskX, rect.maskX + rect.maskWidth],
    [bond.from.y, bond.to.y, rect.maskY, rect.maskY + rect.maskHeight]
  ]) {
    const delta = b! - a!;
    if (Math.abs(delta) < 1e-10) {
      if (a! < min! || a! > max!) return false;
      continue;
    }
    const start = (min! - a!) / delta,
      end = (max! - a!) / delta;
    lo = Math.max(lo, Math.min(start, end));
    hi = Math.min(hi, Math.max(start, end));
    if (lo > hi) return false;
  }
  return true;
}

interface PlacementOptions {
  readonly radii?: readonly number[];
  readonly angleGroups?: readonly (readonly number[])[];
  readonly preferDefault?: boolean;
  readonly padding?: number;
  readonly extraObstacles?: readonly AtomLabelBox[];
  readonly keepNearestNode?: boolean;
  readonly radicalFontSize?: number;
  readonly numberBadge?: boolean;
}

/** Deterministic local placement shared by the canvas and exports. Chemistry is unchanged. */
export function placeChargeLabels(
  atoms: readonly ChargeAtom[],
  bonds: readonly ChargeBond[],
  scale: number,
  fontSize = 15 * scale,
  options: PlacementOptions = {}
): ReadonlyMap<string, ChargePlacement> {
  const obstacles = atoms.flatMap((atom) =>
    [
      ...atom.hulls,
      ...(atom.radical
        ? labelTextHulls(
            atom.radical,
            options.radicalFontSize ?? fontSize,
            atom.x - atom.offsetX * scale,
            atom.y - 9 * scale
          )
        : [])
    ].map((hull) => bounds(hull))
  );
  obstacles.push(...(options.extraObstacles ?? []));
  const placed = new Map<string, ChargePlacement>();
  // Stable ID order prevents drawing-order changes from swapping adjacent charge placements.
  for (const atom of [...atoms].filter((a) => a.text).sort((a, b) => a.id.localeCompare(b.id))) {
    const relative = options.numberBadge
      ? nodeNumberBadgeBox(atom.text, scale)
      : bounds(labelTextHulls(atom.text, fontSize, 0, 0).flat());
    const candidates: Point[] =
      options.preferDefault === false ? [] : [{ x: atom.x + atom.offsetX * scale, y: atom.y - 9 * scale }];
    // Prefer superscript space, then side/below positions only if every upper slot is crowded.
    for (const angles of options.angleGroups ?? [
      [-45, -135, -90, -15, -165],
      [0, 180, 45, 135, 90]
    ])
      for (const radius of options.radii ?? [14, 18, 22, 26, 30])
        for (const degrees of angles) {
          const angle = (degrees * Math.PI) / 180;
          candidates.push({
            x: atom.x + Math.cos(angle) * radius * scale,
            y: atom.y + Math.sin(angle) * radius * scale
          });
        }
    let best: ChargePlacement | undefined,
      bestScore = Infinity;
    for (const point of candidates) {
      const box = { ...relative, maskX: relative.maskX + point.x, maskY: relative.maskY + point.y };
      const padded = expanded(box, (options.padding ?? 2.5) * scale);
      let score = 0;
      if (options.keepNearestNode) {
        const ownDistance = Math.hypot(point.x - atom.x, point.y - atom.y);
        for (const other of atoms)
          if (other.id !== atom.id && Math.hypot(point.x - other.x, point.y - other.y) < ownDistance - 1e-6)
            score += 10000;
      }
      for (const obstacle of obstacles) {
        const area = overlap(padded, obstacle);
        if (area > 0) score += 100 + area / (scale * scale);
      }
      for (const bond of bonds) if (bondCrossesCharge(bond, padded)) score += 1000;
      if (score < bestScore) {
        best = { ...point, box };
        bestScore = score;
      }
      if (score === 0) break;
    }
    if (best) {
      placed.set(atom.id, best);
      obstacles.push(best.box);
    }
  }
  return placed;
}

/** Shared with rendering so collision checks include the outline, not only the digits. */
export function nodeNumberBadgeBox(text: string, scale: number): AtomLabelBox {
  const width = Math.max(14, text.length * 7 + 6) * scale;
  return { maskX: -width / 2, maskY: -8 * scale, maskWidth: width, maskHeight: 16 * scale };
}

/** Number badges use the same collision rules, but stay within 22 scaled pixels of their node. */
export function placeNodeNumbers(
  atoms: readonly ChargeAtom[],
  bonds: readonly ChargeBond[],
  scale: number,
  chargeBoxes: readonly AtomLabelBox[]
): ReadonlyMap<string, ChargePlacement> {
  return placeChargeLabels(
    atoms.map((atom, index) => ({ ...atom, text: String(index + 1) })),
    bonds,
    scale,
    11 * scale,
    {
      radii: [10, 13, 16, 19, 22],
      angleGroups: [[-45, -135, 45, 135, -90, 90, 0, 180, -22.5, -67.5, -112.5, -157.5, 22.5, 67.5, 112.5, 157.5]],
      preferDefault: false,
      padding: 1.5,
      extraObstacles: chargeBoxes,
      keepNearestNode: true,
      radicalFontSize: 15 * scale,
      numberBadge: true
    }
  );
}

/** #1047 -- "(R)" / "(S)" beside each stereocentre: the same collision rules as charges and node
 *  numbers, kept near their atom and clear of both. `descriptors` is by atom id. */
export function placeStereoLabels(
  atoms: readonly ChargeAtom[],
  bonds: readonly ChargeBond[],
  scale: number,
  obstacles: readonly AtomLabelBox[],
  descriptors: Readonly<Record<string, string>>
): ReadonlyMap<string, ChargePlacement> {
  return placeChargeLabels(
    atoms.map((atom) => ({ ...atom, text: descriptors[atom.id] ? `(${descriptors[atom.id]})` : '' })),
    bonds,
    scale,
    12 * scale,
    {
      radii: [12, 15, 18, 21, 24],
      angleGroups: [[45, 135, -45, -135, 90, -90, 0, 180, 22.5, 67.5, 112.5, 157.5, -22.5, -67.5, -112.5, -157.5]],
      preferDefault: false,
      padding: 1.5,
      extraObstacles: obstacles,
      keepNearestNode: true
    }
  );
}

/**
 * #780 (owner): where each double bond's E/Z label goes -- beside the bond's middle, on the side its
 * substituents leave free (a Z bond's label goes away from both, not into the U they make), clear of
 * every label so far and of the bonds; the other side if that one is taken; else wherever
 * {@link placeStereoLabels} finds room.
 */
export function placeBondStereoLabels(
  entries: readonly { readonly id: string; readonly from: Point; readonly to: Point; readonly text: string }[],
  bonds: readonly ChargeBond[],
  scale: number,
  obstacles: readonly AtomLabelBox[]
): ReadonlyMap<string, ChargePlacement> {
  const placed = new Map<string, ChargePlacement>();
  const taken = [...obstacles];
  const near = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y) < 0.5;
  for (const entry of entries) {
    const dx = entry.to.x - entry.from.x,
      dy = entry.to.y - entry.from.y;
    const length = Math.hypot(dx, dy) || 1;
    const normal = { x: -dy / length, y: dx / length };
    const middle = { x: (entry.from.x + entry.to.x) / 2, y: (entry.from.y + entry.to.y) / 2 };
    // Which side the substituents are on: the far end of every other bond at either end.
    let lean = 0;
    for (const bond of bonds) {
      if (
        (near(bond.from, entry.from) && near(bond.to, entry.to)) ||
        (near(bond.from, entry.to) && near(bond.to, entry.from))
      ) {
        continue;
      }
      const far =
        near(bond.from, entry.from) || near(bond.from, entry.to)
          ? bond.to
          : near(bond.to, entry.from) || near(bond.to, entry.to)
            ? bond.from
            : null;
      if (far) lean += Math.sign((far.x - middle.x) * normal.x + (far.y - middle.y) * normal.y);
    }
    const width = entry.text.length * 7 * scale,
      height = 12 * scale,
      distance = 13 * scale;
    const sides = lean > 0 ? [-1, 1] : [1, -1];
    const spot = sides
      .map((side) => {
        const x = middle.x + normal.x * distance * side,
          y = middle.y + normal.y * distance * side;
        return { x, y, box: { maskX: x - width / 2, maskY: y - height / 2, maskWidth: width, maskHeight: height } };
      })
      .find(
        (candidate) =>
          !taken.some((box) => overlap(box, candidate.box) > 0) &&
          !bonds.some((bond) => bondCrossesCharge(bond, candidate.box))
      );
    const placement =
      spot ??
      placeStereoLabels(
        [{ id: entry.id, x: middle.x, y: middle.y, text: '', offsetX: 0, hulls: [] }],
        bonds,
        scale,
        taken,
        {
          [entry.id]: entry.text.replace(/[()]/g, '')
        }
      ).get(entry.id);
    if (placement) {
      placed.set(entry.id, placement);
      taken.push(placement.box);
    }
  }
  return placed;
}
